'use server';

import { PrismaClient } from '@prisma/client';
import { Pool } from 'pg';
import { PrismaPg } from '@prisma/adapter-pg';
import { revalidatePath } from 'next/cache';
import { ContactRequestFormSchema } from './definitions';
import {
  sendContactRequestAutoReply,
  sendOpportunityCreatedWhatsAppNotification,
} from './whatsapp';

const pool = new Pool({
  connectionString: process.env.DATABASE_URL,
});
const adapter = new PrismaPg(pool);
const prisma = new PrismaClient({ adapter });

export type State = {
  errors?: {
    name?: string[];
    companyName?: string[];
    vatNumber?: string[];
    email?: string[];
    phone?: string[];
    goal?: string[];
    privacyAccepted?: string[];
    sourceData?: string[];
  };
  message?: string | null;
  success?: boolean;
};

type SourceData = {
  sourceLabel?: string;
  landingPath?: string;
  landingUrl?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  capturedAt?: string;
};

function parseSourceData(rawValue: FormDataEntryValue | null): SourceData | null {
  if (typeof rawValue !== 'string' || !rawValue.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      sourceLabel:
        typeof parsed.sourceLabel === 'string' ? parsed.sourceLabel.trim().slice(0, 120) : undefined,
      landingPath:
        typeof parsed.landingPath === 'string' ? parsed.landingPath.trim().slice(0, 300) : undefined,
      landingUrl:
        typeof parsed.landingUrl === 'string' ? parsed.landingUrl.trim().slice(0, 500) : undefined,
      referrer:
        typeof parsed.referrer === 'string' ? parsed.referrer.trim().slice(0, 500) : undefined,
      utmSource:
        typeof parsed.utmSource === 'string' ? parsed.utmSource.trim().slice(0, 120) : undefined,
      utmMedium:
        typeof parsed.utmMedium === 'string' ? parsed.utmMedium.trim().slice(0, 120) : undefined,
      utmCampaign:
        typeof parsed.utmCampaign === 'string' ? parsed.utmCampaign.trim().slice(0, 120) : undefined,
      utmTerm:
        typeof parsed.utmTerm === 'string' ? parsed.utmTerm.trim().slice(0, 120) : undefined,
      utmContent:
        typeof parsed.utmContent === 'string' ? parsed.utmContent.trim().slice(0, 120) : undefined,
      capturedAt:
        typeof parsed.capturedAt === 'string' ? parsed.capturedAt.trim().slice(0, 60) : undefined,
    };
  } catch {
    return null;
  }
}

function buildTrafficSummary(sourceData: SourceData | null): string | null {
  if (!sourceData) {
    return null;
  }

  return (
    sourceData.sourceLabel ||
    sourceData.utmSource ||
    sourceData.referrer ||
    sourceData.landingPath ||
    null
  );
}

export async function createContactRequest(prevState: State, formData: FormData) {
  const validatedFields = ContactRequestFormSchema.safeParse({
    name: formData.get('name'),
    companyName: formData.get('companyName'),
    vatNumber: formData.get('vatNumber'),
    email: formData.get('email'),
    phone: formData.get('phone'),
    goal: formData.get('goal'),
    privacyAccepted: formData.get('privacyAccepted') === 'accepted',
    sourceData: formData.get('sourceData'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Errore nell\'invio della richiesta.',
    };
  }

  const { name, companyName, vatNumber, email, phone, goal, sourceData } = validatedFields.data;
  const normalizedPhone = phone?.trim() || null;
  const normalizedCompanyName = companyName.trim();
  const normalizedVatNumber = vatNumber.trim().toUpperCase();
  const normalizedGoal = goal.trim();
  const parsedSourceData = parseSourceData(sourceData ?? null);
  const trafficSummary = buildTrafficSummary(parsedSourceData);
  const goalPreview = normalizedGoal.slice(0, 72);
  const opportunityTitle = `${normalizedCompanyName} - ${goalPreview}${normalizedGoal.length > 72 ? '...' : ''}`;
  const leadName = `${normalizedCompanyName} (${name})`;
  const contactRequestMessage = [
    'Richiesta B2B arrivata dal form contatti del sito.',
    `Referente: ${name}`,
    `Azienda: ${normalizedCompanyName}`,
    `P.IVA: ${normalizedVatNumber}`,
    `Email: ${email}`,
    normalizedPhone ? `Telefono: ${normalizedPhone}` : null,
    trafficSummary ? `Provenienza: ${trafficSummary}` : null,
    '',
    'Cosa vorrebbe ottenere:',
    normalizedGoal,
    '',
    'Dettagli provenienza:',
    parsedSourceData?.landingPath ? `Landing iniziale: ${parsedSourceData.landingPath}` : null,
    parsedSourceData?.landingUrl ? `URL iniziale: ${parsedSourceData.landingUrl}` : null,
    parsedSourceData?.referrer ? `Referrer: ${parsedSourceData.referrer}` : 'Referrer: Diretto / non disponibile',
    parsedSourceData?.utmSource ? `UTM source: ${parsedSourceData.utmSource}` : null,
    parsedSourceData?.utmMedium ? `UTM medium: ${parsedSourceData.utmMedium}` : null,
    parsedSourceData?.utmCampaign ? `UTM campaign: ${parsedSourceData.utmCampaign}` : null,
    parsedSourceData?.utmTerm ? `UTM term: ${parsedSourceData.utmTerm}` : null,
    parsedSourceData?.utmContent ? `UTM content: ${parsedSourceData.utmContent}` : null,
    parsedSourceData?.capturedAt ? `Rilevato il: ${parsedSourceData.capturedAt}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  try {
    const createdOpportunity = await prisma.$transaction(async (tx) => {
      await tx.contactRequest.create({
        data: {
          name,
          email,
          phone: normalizedPhone,
          subject: opportunityTitle,
          message: contactRequestMessage,
          status: 'NEW',
        },
      });

      const client = await tx.client.findFirst({
        where: {
          OR: [
            { email },
            { contacts: { some: { email } } },
          ],
        },
        select: { id: true },
      });

      let leadId: string | null = null;

      if (!client) {
        const leadLookup: Array<{ email?: string; phone?: string }> = [{ email }];
        if (normalizedPhone) {
          leadLookup.push({ phone: normalizedPhone });
        }

        const existingLead = await tx.lead.findFirst({
          where: { OR: leadLookup },
          orderBy: { createdAt: 'desc' },
          select: {
            id: true,
            phone: true,
            source: true,
          },
        });

        if (existingLead) {
          leadId = existingLead.id;

          if ((!existingLead.phone && normalizedPhone) || !existingLead.source) {
            await tx.lead.update({
              where: { id: existingLead.id },
              data: {
                phone: existingLead.phone || normalizedPhone,
                source: existingLead.source || trafficSummary || 'Form contatti sito',
              },
            });
          }
        } else {
          const lead = await tx.lead.create({
            data: {
              name: leadName,
              email,
              phone: normalizedPhone,
              status: 'NEW',
              source: trafficSummary || 'Form contatti sito',
            },
            select: { id: true },
          });

          leadId = lead.id;
        }
      }

      return tx.opportunity.create({
        data: {
          title: opportunityTitle,
          value: 0,
          status: 'OPEN',
          clientId: client?.id || null,
          leadId,
          description: contactRequestMessage,
        },
        include: {
          client: {
            select: {
              name: true,
            },
          },
          lead: {
            select: {
              name: true,
            },
          },
        },
      });
    });

    await sendOpportunityCreatedWhatsAppNotification({
      id: createdOpportunity.id,
      title: createdOpportunity.title,
      value: createdOpportunity.value,
      status: createdOpportunity.status,
      clientName: createdOpportunity.client?.name,
      leadName: createdOpportunity.lead?.name,
      description: createdOpportunity.description,
      source: 'Form contatti sito',
      trafficSource: trafficSummary,
    });

    await sendContactRequestAutoReply(normalizedPhone, name);
  } catch (error) {
    console.error('Database Error:', error);
    return {
      message: 'Errore nel database: Impossibile inviare la richiesta.',
    };
  }

  revalidatePath('/gestionale/dashboard/contact-requests');
  revalidatePath('/gestionale/dashboard/leads');
  revalidatePath('/gestionale/dashboard/opportunities');
  return { message: 'Richiesta inviata con successo!', success: true };
}

export async function deleteContactRequest(id: string) {
  try {
    await prisma.contactRequest.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/contact-requests');
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Impossibile eliminare la richiesta.');
  }
}

export async function markContactRequestAsRead(id: string) {
  try {
    await prisma.contactRequest.update({
      where: { id },
      data: { status: 'READ' },
    });
    revalidatePath('/gestionale/dashboard/contact-requests');
  } catch (error) {
     console.error('Database Error:', error);
     throw new Error('Impossibile aggiornare la richiesta.');
  }
}
